import dayjs from 'dayjs'
import {useCallback, useMemo} from 'react'
import {useController, useFormContext} from 'react-hook-form'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Trash01Icon} from '@/components/ui/icon/Icon'
import {DiagnosisCodesLinkToggle} from '@/features/projects/components/diagnosis-codes-link-toggle/DiagnosisCodesLinkToggle'
import {InputSelectPathology} from '@/features/projects/components/input-select-pathology/InputSelectPathology'
import {MainPathologiesFormSchema} from '@/features/projects/formSchemas'
import {DiagnosisCodesPair, useDiagnosisCodesLink} from '@/features/projects/hooks/useDiagnosisCodesLink'
import {useTranslation} from '@/translations/i18n'

interface ProjectMainPathologiesFieldsProps {
    index: number
    disabledIcd9: string[]
    disabledIcd10: string[]
    showTrashButton: boolean
    remove: (index: number) => void
}

export const ProjectMainPathologiesFields = ({
    index,
    disabledIcd9,
    disabledIcd10,
    showTrashButton,
    remove
}: ProjectMainPathologiesFieldsProps) => {
    const {t} = useTranslation()
    const {control} = useFormContext<MainPathologiesFormSchema>()
    const {field} = useController({control, name: `mainPathologies.${index}`})

    const selectedStartDate = useMemo(() => {
        return field.value.startedAt ? dayjs(field.value.startedAt).toDate() : null
    }, [field.value])

    const handleChangeCodes = useCallback(
        ({icd9, icd10}: DiagnosisCodesPair) => {
            field.onChange({...field.value, diagnosis: icd9, icd10Diagnosis: icd10}, {shouldDirty: true})
        },
        [field.onChange, field.value]
    )

    // The two codes stay separate fields: it is the chain, not the schema, that keeps them aligned.
    // From 2027 ICD-10 is the only classification that may be picked, so ICD-9 stays visible but is
    // filled in from the ICD-10 rather than searched.
    const {isLinked, isIcd9Selectable, onToggleLink, handleSelectIcd9, handleSelectIcd10} = useDiagnosisCodesLink({
        icd9: field.value.diagnosis,
        icd10: field.value.icd10Diagnosis,
        onChange: handleChangeCodes
    })

    const handleSelectStartDate = useCallback(
        (value: Date | null) => {
            field.onChange(
                {...field.value, startedAt: value ? dayjs(value).format('YYYY-MM-DD') : null},
                {
                    shouldDirty: true
                }
            )
        },
        [field.onChange, field.value]
    )

    return (
        <Flexbox fullWidth justify="space-between" gap={2.5}>
            <div style={{flex: '1 1 0', minWidth: 0}}>
                <InputSelectPathology
                    label={t('projects:pathology_number_icd9_cm', {number: index + 1})}
                    placeholder={t('projects:pathology_number_placeholder')}
                    value={field.value.diagnosis}
                    onChange={handleSelectIcd9}
                    disabledOptions={disabledIcd9}
                    disabled={!isIcd9Selectable}
                    isClearable
                    version="ICD9"
                />
            </div>
            <DiagnosisCodesLinkToggle isLinked={isLinked} isEditable={isIcd9Selectable} onToggle={onToggleLink} />
            <div style={{flex: '1 1 0', minWidth: 0}}>
                <InputSelectPathology
                    label={t('projects:pathology_number_icd10_im', {number: index + 1})}
                    placeholder={t('projects:pathology_number_icd10_im_placeholder')}
                    value={field.value.icd10Diagnosis}
                    onChange={handleSelectIcd10}
                    disabledOptions={disabledIcd10}
                    isClearable
                    version="ICD10"
                />
            </div>
            <Flexbox gap={1} align="flex-end" style={{flex: '0 0 auto'}}>
                <InputDate
                    label={t('projects:start_date')}
                    placeholder={'GG/MM/AAAA'}
                    value={selectedStartDate}
                    onChange={handleSelectStartDate}
                    onMonthChange={handleSelectStartDate}
                    disableAfterDate={new Date()}
                />
                {showTrashButton && (
                    <Button variant="ghost" shape="square" onClick={() => remove(index)}>
                        <Trash01Icon />
                    </Button>
                )}
            </Flexbox>
        </Flexbox>
    )
}
